/** Dialog: a modal for one short question or form. Framed like a card, centred on its shadow (no scrim: the page behind is inert); opens and closes like a menu. */
import type { ReactNode } from "react";
import { Dialog as Primitive } from "radix-ui";
import { Button } from "./Button";
import { CloseIcon } from "./CloseIcon";
import { cx } from "./cx";
import { useFocusReturn } from "./overlay";

const SIZE = { sm: "max-w-sm", md: "max-w-lg" };

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** The buttons, right-aligned: the primary one last. */
  footer?: ReactNode;
  size?: keyof typeof SIZE;
  children?: ReactNode;
}

export function Dialog({ open, onClose, title, description, footer, size = "sm", children }: DialogProps) {
  const focus = useFocusReturn({ first: () => document.querySelector<HTMLElement>("[data-slot=dialog] [data-autofocus]") });
  return (
    <Primitive.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Primitive.Portal>
        {/* Centred by its margins, so `transform` stays free for the surface's own motion. */}
        <Primitive.Content
          data-slot="dialog"
          {...(description ? {} : { "aria-describedby": undefined })}
          tabIndex={-1}
          {...focus}
          className={cx(
            "fixed inset-0 z-50 m-auto flex h-fit max-h-[calc(100dvh-32px)] w-[calc(100%-32px)] flex-col rounded-xl bg-muted p-1 shadow-popover outline-none data-[state=open]:animate-menu-in data-[state=closed]:animate-menu-out",
            SIZE[size],
          )}
        >
            <header className="flex shrink-0 items-start justify-between gap-3 px-3 py-2.5">
              <div className="flex min-w-0 flex-col gap-1 py-1">
                <Primitive.Title className="text-heading font-medium text-foreground">{title}</Primitive.Title>
                {description && <Primitive.Description className="text-meta text-secondary-foreground">{description}</Primitive.Description>}
              </div>
              <Button variant="ghost" size="icon-sm" aria-label="Close" onClick={onClose}>
                <CloseIcon />
              </Button>
            </header>
            {children && <div className="flex min-h-0 flex-col gap-3 overflow-y-auto rounded-xl border-hair border-input bg-card px-4 pb-4 pt-5">{children}</div>}
            {footer && <footer className="flex shrink-0 items-center justify-end gap-2 px-2 pb-1.5 pt-2.5">{footer}</footer>}
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );
}
